import {
  useId,
  type ComponentPropsWithRef,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { AlertCircle } from "lucide-react";

/**
 * Campo de formulário com rótulo, ajuda e erro associados programaticamente
 * (AGENTS §14.1). O erro aparece junto do campo, no padrão COSMOS, e nunca
 * depende só de cor: vem com ícone e texto.
 *
 * `Field` entrega os ids prontos para o controle filho, então o mesmo invólucro
 * serve para input, textarea, Select COSMOS e controles compostos.
 */
export interface FieldRenderArgs {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

export interface FieldProps {
  label: string;
  /** Texto de apoio permanente. */
  help?: ReactNode;
  /** Mensagem de erro; presença marca o campo como inválido. */
  error?: string;
  required?: boolean;
  id?: string;
  className?: string;
  children: (args: FieldRenderArgs) => ReactNode;
}

export function Field({
  label,
  help,
  error,
  required = false,
  id,
  className,
  children,
}: FieldProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const helpId = help ? `${fieldId}-help` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={className ? `cx-field ${className}` : "cx-field"}>
      <label className="cx-field-label" htmlFor={fieldId} data-required={required || undefined}>
        {label}
      </label>
      {children({ id: fieldId, describedBy, invalid: Boolean(error) })}
      {help && (
        <span id={helpId} className="cx-field-help">
          {help}
        </span>
      )}
      {error && (
        <span id={errorId} className="cx-field-error">
          <AlertCircle size={13} aria-hidden="true" />
          {error}
        </span>
      )}
    </div>
  );
}

export type TextInputProps = ComponentPropsWithRef<"input">;

export function TextInput({ className, ...rest }: TextInputProps) {
  return <input {...rest} className={className ? `cx-input ${className}` : "cx-input"} />;
}

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export function TextArea({ className, ...rest }: TextAreaProps) {
  return (
    <textarea {...rest} className={className ? `cx-textarea ${className}` : "cx-textarea"} />
  );
}
